import { ADMIN_EMAIL, contactMailto } from '../lib/config'

const EFFECTIVE_DATE = 'May 31, 2026'
const SERVICE_URL = 'getproofofwork.com'

export default function TermsPage({ embedded = false }: { embedded?: boolean }) {
  const body = (
    <div className="space-y-10">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-stone-900">Terms of Service</h1>
          <p className="text-stone-500 text-sm">Proof of Work — Your record of professional impact</p>
          <p className="text-stone-400 text-xs pt-1">Effective {EFFECTIVE_DATE}</p>
        </div>

        <Section title="Agreement to these terms">
          <p>
            These Terms of Service (&ldquo;Terms&rdquo;) govern your access to and use of
            Proof of Work ({SERVICE_URL}), operated by its individual developer based in Israel
            (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;the Operator&rdquo;). By creating an
            account or using the Service, you agree to be bound by these Terms. If you do not
            agree, do not use the Service.
          </p>
        </Section>

        <Section title="Eligibility">
          <p>
            You must be at least 18 years old and have the legal capacity to enter into a
            binding agreement in your jurisdiction to use this Service. By creating an account,
            you confirm that you meet these requirements.
          </p>
          <p className="mt-2">
            If you are using the Service in a professional capacity on behalf of an organisation,
            you represent that you have the authority to bind that organisation to these Terms,
            and that &ldquo;you&rdquo; in these Terms refers to that organisation.
          </p>
        </Section>

        <Section title="Account registration">
          <p>
            Access to the Service requires authentication through Google Sign-in. You are
            responsible for maintaining the security of your Google account and for all
            activity that occurs through it. Notify us immediately at{' '}
            <a href={contactMailto()}
               className="text-stone-700 underline underline-offset-2">{ADMIN_EMAIL}</a>{' '}
            if you become aware of any unauthorised access.
          </p>
          <p className="mt-2">
            Each person may hold only one account. Creating multiple accounts is not permitted.
          </p>
        </Section>

        <Section title="Description of the service">
          <p>
            Proof of Work is a professional CV builder that stores your career accomplishments
            as structured records and generates tailored CVs from them using AI. The Service
            includes:
          </p>
          <ul className="list-disc pl-4 space-y-1 mt-2">
            <li>A Library where you add, edit, and organise your professional accomplishments</li>
            <li>CV generation powered by AI, using your Library as the source material</li>
            <li>Export of generated CVs as .docx files</li>
            <li>Settings for profile management, data export, account deletion, and optionally supplying your own AI provider API key</li>
          </ul>
          <p className="mt-2">
            We reserve the right to modify, expand, or reduce the features of the Service at
            any time. For changes that materially reduce functionality, we will give reasonable
            advance notice by email.
          </p>
        </Section>

        <Section title="Your content">
          <p>
            <strong>Ownership:</strong> You retain full ownership of all professional content
            you submit to the Service — your accomplishments, CV documents, uploaded materials,
            and profile information (&ldquo;Your Content&rdquo;). We do not claim ownership
            over Your Content.
          </p>
          <p className="mt-2">
            <strong>Licence to us:</strong> You grant us a limited, non-exclusive, worldwide
            licence to store, process, and display Your Content solely as necessary to operate
            the Service for you. This licence ends when you delete the relevant content or
            delete your account.
          </p>
          <p className="mt-2">
            <strong>Your responsibility:</strong> You are solely responsible for the accuracy,
            completeness, and legality of Your Content. Do not upload documents that contain
            confidential information belonging to a third party — such as a former employer —
            unless you have the right to use that information. Do not misrepresent your
            professional history.
          </p>
        </Section>

        <Section title="Acceptable use">
          <p>You agree not to:</p>
          <ul className="list-disc pl-4 space-y-1 mt-2">
            <li>Use the Service in any way that violates applicable law or regulation</li>
            <li>Upload content that infringes the intellectual property rights or trade secrets of any third party</li>
            <li>Use the Service to create CVs for or on behalf of third parties without their knowledge and consent</li>
            <li>Attempt to reverse-engineer, decompile, scrape, or automate access to the Service</li>
            <li>Probe, scan, or test the security of the Service or its infrastructure without our written permission</li>
            <li>Impersonate another person or provide false registration information</li>
            <li>Circumvent, disable, or interfere with any security feature of the Service</li>
          </ul>
        </Section>

        <Section title="AI-generated content">
          <p>
            CV content generated by the Service is produced by AI models. While we design
            the system to generate accurate, professional output, we do not guarantee that
            AI-generated content is accurate, complete, free of error, or appropriate for any
            particular purpose.
          </p>
          <p className="mt-2">
            <strong>You are responsible for reviewing all generated CVs</strong> before
            submitting them to employers or any other parties. Never submit AI-generated
            content you have not read and verified.
          </p>
          <p className="mt-2">
            When using our managed API key, your content is processed by our current AI
            provider. We reserve the right to change AI providers. When using your own API key,
            you are subject to that provider&apos;s terms of service in addition to these Terms.
          </p>
        </Section>

        <Section title="Pricing and billing">
          <p>
            <strong>Current status:</strong> The Service is currently available during an
            early-access period. Any applicable pricing is displayed on the Service website.
          </p>
          <p className="mt-2">
            <strong>Managed API key:</strong> When you use our managed API key, AI processing
            costs are borne by us up to any limits stated on the Service website. We reserve
            the right to introduce usage limits or pricing with reasonable advance notice.
          </p>
          <p className="mt-2">
            <strong>Your own API key:</strong> If you configure your own AI provider API key
            in Settings, you are solely responsible for all costs charged by that provider.
            We are not liable for charges you incur with a provider you have independently
            configured.
          </p>
          <p className="mt-2">
            <strong>Introducing paid plans:</strong> If we introduce a subscription fee or
            paid tier, we will give at least 30 days&apos; advance notice by email before any
            charges apply to your account.
          </p>
        </Section>

        <Section title="Disclaimer of warranties">
          <p className="uppercase text-xs tracking-wide font-semibold text-stone-500 mb-2">
            Important — please read carefully
          </p>
          <p>
            The Service is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo; without
            any warranties of any kind, express or implied, including without limitation
            warranties of merchantability, fitness for a particular purpose, or
            non-infringement.
          </p>
          <p className="mt-2">
            We do not warrant that the Service will be uninterrupted, error-free, secure, or
            that any defects will be corrected. We do not warrant that the Service or any
            AI-generated output will meet your requirements or expectations. You use the
            Service at your own risk.
          </p>
        </Section>

        <Section title="Limitation of liability">
          <p className="uppercase text-xs tracking-wide font-semibold text-stone-500 mb-2">
            Important — please read carefully
          </p>
          <p>
            To the fullest extent permitted by applicable law, we will not be liable to you
            for any indirect, incidental, special, consequential, or punitive damages arising
            from your use of or inability to use the Service — including but not limited to
            loss of profits, loss of data, loss of employment opportunity, or business
            interruption — even if we have been advised of the possibility of such damages.
          </p>
          <p className="mt-2">
            Our total aggregate liability to you for any and all claims arising from or
            related to the Service shall not exceed the greater of: (a) the total fees you
            paid to us in the 12 months immediately preceding the event giving rise to the
            claim, or (b) USD 50.
          </p>
          <p className="mt-2">
            Nothing in these Terms limits or excludes liability for: (a) death or personal
            injury caused by our negligence; (b) fraud or fraudulent misrepresentation; or
            (c) any other liability that cannot be excluded or limited under applicable
            mandatory law.
          </p>
        </Section>

        <Section title="Indemnification">
          <p>
            You agree to defend, indemnify, and hold us harmless from any claim, loss,
            liability, damage, or expense (including reasonable legal fees) arising from:
            (a) your use of the Service; (b) Your Content; (c) your violation of these Terms;
            or (d) your violation of any third-party right, including intellectual property
            rights or privacy rights.
          </p>
        </Section>

        <Section title="Termination">
          <p>
            <strong>By you:</strong> You may stop using the Service and permanently delete
            your account at any time via Settings → Data → Delete My Account. Deletion is
            immediate and irreversible.
          </p>
          <p className="mt-2">
            <strong>By us:</strong> We may suspend or terminate your access at any time if:
            (a) you breach these Terms; (b) we are required to do so by applicable law; or
            (c) we decide to discontinue the Service entirely. Where we terminate for reasons
            other than your breach, we will give reasonable advance notice by email.
          </p>
          <p className="mt-2">
            On termination, your data will be handled in accordance with the Privacy Policy.
          </p>
        </Section>

        <Section title="Governing law and jurisdiction">
          <p>
            These Terms are governed by and construed in accordance with the laws of the
            State of Israel, without regard to its conflict of law principles. Any dispute
            arising out of or in connection with these Terms or your use of the Service shall
            be subject to the exclusive jurisdiction of the competent courts located in
            Israel.
          </p>
          <p className="mt-2">
            If you are located in the European Union, you may also have the right to bring
            proceedings in the courts of your country of residence.
          </p>
        </Section>

        <Section title="Severability">
          <p>
            If any provision of these Terms is found by a court of competent jurisdiction to
            be unenforceable or invalid, that provision will be limited or eliminated to the
            minimum extent necessary, and the remaining provisions will continue in full force
            and effect.
          </p>
        </Section>

        <Section title="Changes to these terms">
          <p>
            If we make material changes to these Terms, we will notify you by email at least
            14 days before the changes take effect. Continued use of the Service after the
            effective date of the revised Terms constitutes your acceptance of those changes.
            The effective date at the top of this page will always reflect the current version.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            For questions about these Terms:{' '}
            <a href={contactMailto()}
               className="text-stone-700 underline underline-offset-2">{ADMIN_EMAIL}</a>
          </p>
        </Section>
    </div>
  )

  // Embedded mode (e.g. the onboarding modal) renders just the terms body; the
  // host supplies the scroll container and dismiss affordance.
  if (embedded) return body

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-12">{body}</main>
      <footer className="py-4 text-center text-xs text-stone-400 border-t border-stone-100">
        <div className="flex items-center justify-center gap-4">
          <a href="/" className="hover:text-stone-600 transition-colors">Back to Proof of Work</a>
          <span>·</span>
          <a href="/privacy" className="hover:text-stone-600 transition-colors">Privacy Policy</a>
        </div>
      </footer>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-base font-medium text-stone-800">{title}</h2>
      <div className="text-stone-600 text-sm leading-relaxed">{children}</div>
    </section>
  )
}
